import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Mail, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "./shared";
import { PHONE, PHONE_HREF, EMAIL, EMAIL_HREF } from "../data/site";
import { LANDING_LIST } from "../data/landing";
import { UserMenu } from "./UserMenu";

function GuidesMenu() {
  return (
    <div className="relative group">
      <button data-testid="nav-guides-btn" className="flex items-center gap-1 whitespace-nowrap text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#F3E5AB] transition-colors">
        Guides <ChevronDown size={12} className="transition-transform group-hover:rotate-180" />
      </button>
      <div className="absolute left-0 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300" data-testid="nav-guides-menu">
        <div className="w-64 bg-black/95 backdrop-blur-md border border-[#D4AF37]/20 rounded-[2px] p-2">
          {LANDING_LIST.map((p) => (
            <Link key={p.slug} to={p.path} data-testid={`nav-guide-${p.slug}`} className="block px-4 py-3 text-[11px] uppercase tracking-[0.18em] text-[#A1A1AA] hover:text-[#F3E5AB] hover:bg-[#D4AF37]/[0.06] rounded-[2px] transition-colors">
              {p.tag.replace(" Guide", "")}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const LINKS = [
  { label: "Gaur Yamuna City", id: "gyc" },
  { label: "Inventory", id: "inventory" },
  { label: "Corridor", id: "corridor" },
  { label: "Airport", id: "airport" },
  { label: "YEIDA Plots", id: "yeida", wide: true },
  { label: "7th Park View", id: "explore-corridor" },
  { label: "Insights", id: "insights", mobileOnly: true },
  { label: "Advisory", id: "advisory", mobileOnly: true },
];

export function Navbar({ onAdvisor }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || open ? "backdrop-blur-md bg-black/90 border-b border-[#D4AF37]/15" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-[1720px] mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden xl:flex items-center gap-5 2xl:gap-6 mx-6">
          {LINKS.filter((l) => !l.mobileOnly).map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollToId(l.id)}
              className={`whitespace-nowrap text-[11px] uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-[#F3E5AB] transition-colors ${l.wide ? "hidden 2xl:inline" : ""}`}
            >
              {l.label}
            </button>
          ))}
          <GuidesMenu />
        </nav>
        <div className="hidden xl:flex items-center gap-4 ml-6">
          <a href={PHONE_HREF} data-testid="nav-call-btn" className="hidden 2xl:flex items-center gap-2 whitespace-nowrap text-[12px] tracking-wider text-[#A1A1AA] hover:text-[#F3E5AB] transition-colors">
            <Phone size={14} className="text-[#D4AF37]" /> {PHONE}
          </a>
          <a href={EMAIL_HREF} data-testid="nav-email-btn" className="hidden min-[1640px]:flex items-center gap-2 whitespace-nowrap text-[12px] tracking-wider text-[#A1A1AA] hover:text-[#F3E5AB] transition-colors">
            <Mail size={14} className="text-[#D4AF37]" /> {EMAIL}
          </a>
          <UserMenu />
          <button
            data-testid="nav-advisor-btn"
            onClick={onAdvisor}
            className="whitespace-nowrap px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black hover:from-[#D4AF37] hover:to-[#F3E5AB] transition-colors rounded-[2px]"
          >
            Speak to an Advisor
          </button>
        </div>
        <button
          data-testid="nav-mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="xl:hidden flex flex-col gap-1.5 p-2"
          aria-label="Menu"
        >
          <span className={`h-px w-6 bg-[#D4AF37] transition-transform ${open ? "rotate-45 translate-y-[3.5px]" : ""}`} />
          <span className={`h-px w-6 bg-[#D4AF37] transition-transform ${open ? "-rotate-45 -translate-y-[3.5px]" : ""}`} />
        </button>
      </div>
      {open && (
        <div className="xl:hidden bg-black/95 backdrop-blur-md border-t border-[#D4AF37]/15 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              onClick={() => { setOpen(false); scrollToId(l.id); }}
              className="text-left text-sm uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-[#F3E5AB]"
            >
              {l.label}
            </button>
          ))}
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-2">Guides</p>
          {LANDING_LIST.map((p) => (
            <Link key={p.slug} to={p.path} data-testid={`mobile-nav-guide-${p.slug}`} onClick={() => setOpen(false)} className="text-left text-sm uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-[#F3E5AB]">
              {p.tag.replace(" Guide", "")}
            </Link>
          ))}
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-2">Account</p>
          <UserMenu compact />
          <button
            data-testid="mobile-nav-advisor-btn"
            onClick={() => { setOpen(false); onAdvisor(); }}
            className="mt-2 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] bg-gradient-to-r from-[#C5A059] to-[#D4AF37] text-black rounded-[2px]"
          >
            Speak to an Advisor
          </button>
          <a href={EMAIL_HREF} data-testid="mobile-nav-email" className="flex items-center gap-2 text-[11px] tracking-wider text-[#A1A1AA] hover:text-[#F3E5AB]">
            <Mail size={13} className="text-[#D4AF37]" /> {EMAIL}
          </a>
        </div>
      )}
    </motion.header>
  );
}
